import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  bulkInviteUsers,
  checkNewEmailStatus,
  checkUserExists,
  createAdminUser,
  deleteAdminUser,
  fetchAdminUserDetail,
  fetchAdminUsers,
  inviteUser,
  recreateAdminUser,
  reinviteUser,
  revokeUser,
  unlockUser,
  updateAdminUser,
} from '../api/admin'
import type { AdminListFilters, AdminUserFormValues } from '../types/admin'
import { queryKeys } from '../api/queryKeys'

export function useAdminUsers(filters: AdminListFilters = {}) {
  return useQuery({
    queryKey: queryKeys.admin.users({
      name: filters.fullName,
      status: filters.hideInactive ? 'active-only' : 'all',
    }),
    queryFn: () => fetchAdminUsers(filters),
  })
}

export function useAdminUserDetail(userId: string) {
  return useQuery({
    queryKey: ['admin', 'user', userId] as const,
    queryFn: () => fetchAdminUserDetail(userId),
    enabled: !!userId,
  })
}

function invalidateAdminUsers(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: queryKeys.admin.all })
}

export function useCreateAdminUserMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: AdminUserFormValues) => createAdminUser(payload),
    onSuccess: () => invalidateAdminUsers(queryClient),
  })
}

export function useRecreateAdminUserMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: AdminUserFormValues) => recreateAdminUser(payload),
    onSuccess: () => invalidateAdminUsers(queryClient),
  })
}

export function useUpdateAdminUserMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: AdminUserFormValues) => updateAdminUser(payload),
    onSuccess: () => invalidateAdminUsers(queryClient),
  })
}

export function useDeleteAdminUserMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => deleteAdminUser(userId),
    onSuccess: () => invalidateAdminUsers(queryClient),
  })
}

export function useReinviteUserMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => reinviteUser(userId),
    onSuccess: () => invalidateAdminUsers(queryClient),
  })
}

export function useInviteUserMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => inviteUser(userId),
    onSuccess: () => invalidateAdminUsers(queryClient),
  })
}

export function useRevokeUserMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => revokeUser(userId),
    onSuccess: () => invalidateAdminUsers(queryClient),
  })
}

export function useUnlockUserMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => unlockUser(userId),
    onSuccess: () => invalidateAdminUsers(queryClient),
  })
}

export function useCheckUserExistsMutation() {
  return useMutation({
    mutationFn: (email: string) => checkUserExists(email),
  })
}

export function useCheckNewEmailStatusMutation() {
  return useMutation({
    mutationFn: (email: string) => checkNewEmailStatus(email),
  })
}

export function useBulkInviteUsersMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userIds: string[]) => bulkInviteUsers(userIds),
    onSuccess: () => invalidateAdminUsers(queryClient),
  })
}
